/**
 * The account's custom domains (T5), a card on Settings → Account access:
 * where respondents reach this account's forms. Per account, form slug in
 * the path — forms.acme.com/order redirects to the real form.
 *
 * Each domain walks: add (customBranding-gated) → set two DNS records
 * (shown here) → Verify (our TXT ownership check, then Render registration
 * and its certificate) → serving. Removal is never gated.
 */
import { useEffect, useState } from 'react';
import {
  CustomDomain, CustomDomainsState, addCustomDomainApi, customDomainStatusApi,
  listCustomDomainsApi, removeCustomDomainApi, verifyCustomDomainApi,
} from '../../api/admin';
import { PlanLimitBody, planLimitOf } from '../../api/limits';
import PlanLimitBlock from '../../components/PlanLimitBlock';
import { useToast } from '../../components/Toast';

const mono: React.CSSProperties = {
  fontFamily: 'var(--font-mono)', fontSize: 12.5, background: 'var(--canvas)',
  border: '1px solid var(--border)', borderRadius: 6, padding: '2px 6px', wordBreak: 'break-all',
};

function statusOf(d: CustomDomain): { label: string; color: string } {
  if (!d.verified_at) return { label: 'Waiting for DNS', color: 'var(--warn-fg)' };
  if (d.render_status === 'verified') return { label: 'Live', color: 'var(--ok-fg)' };
  return { label: 'Verified — certificate pending', color: 'var(--warn-fg)' };
}

export default function CustomDomainsCard({ canManage }: { canManage: boolean }) {
  const [state, setState] = useState<CustomDomainsState | null>(null);
  const [adding, setAdding] = useState('');
  const [error, setError] = useState('');
  const [limitHit, setLimitHit] = useState<PlanLimitBody | null>(null);
  const [busyId, setBusyId] = useState<number | 'add' | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<number | null>(null);
  const toast = useToast();

  const load = () => listCustomDomainsApi().then(setState).catch((e) => setError(e.message));
  useEffect(() => { void load(); }, []);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    setBusyId('add'); setError(''); setLimitHit(null);
    try {
      await addCustomDomainApi(adding.trim());
      setAdding('');
      await load();
    } catch (err: any) {
      const limit = planLimitOf(err);
      if (limit) setLimitHit(limit); else setError(err.message);
    } finally { setBusyId(null); }
  }

  async function verify(d: CustomDomain) {
    setBusyId(d.id); setError('');
    try {
      await verifyCustomDomainApi(d.id);
      toast(`${d.domain} is verified — the certificate follows within minutes.`);
      await load();
    } catch (err: any) {
      setError(err.message);
      await load();
    } finally { setBusyId(null); }
  }

  async function refresh(d: CustomDomain) {
    setBusyId(d.id);
    try { await customDomainStatusApi(d.id); await load(); } finally { setBusyId(null); }
  }

  async function remove(d: CustomDomain) {
    setBusyId(d.id); setConfirmRemove(null); setError('');
    try {
      await removeCustomDomainApi(d.id);
      toast(`${d.domain} removed — links on it stop working.`);
      await load();
    } catch (err: any) { setError(err.message); } finally { setBusyId(null); }
  }

  if (!state) return null;
  // An install (or a test stub) that answers without the fields reads as
  // unconfigured rather than crashing the page around this card.
  const domains = state.domains || [];
  const configured = !!state.configured;

  return (
    <div className="fs-card" id="st-domains">
      <div className="fs-card-head">
        <h2>Custom domain</h2>
        <div className="hint">
          Serve your forms from your own domain — forms.your-company.com/order instead of
          form-magic.com. The domain carries every form on this account; certificates are
          issued and renewed automatically.
        </div>
      </div>
      <div style={{ padding: '14px 20px 18px' }}>
        {error && <div className="ad-err" style={{ marginBottom: 10 }}>{error}</div>}
        {limitHit && <div style={{ marginBottom: 12 }}><PlanLimitBlock body={limitHit} /></div>}

        {!configured && (
          <div className="fs-consequence">
            Custom domains are not set up on this install yet.
          </div>
        )}

        {domains.map((d) => {
          const s = statusOf(d);
          return (
            <div key={d.id} style={{ borderTop: '1px solid var(--border-soft)', padding: '12px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <b style={{ fontSize: 14 }}>{d.domain}</b>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: s.color }}>{s.label}</span>
                <span style={{ flex: 1 }} />
                {canManage && d.verified_at && d.render_status !== 'verified' && (
                  <button className="fc-act" disabled={busyId === d.id} onClick={() => void refresh(d)}>Check certificate</button>
                )}
                {canManage && !d.verified_at && (
                  <button className="ad-btn small primary" disabled={busyId === d.id} onClick={() => void verify(d)}>
                    {busyId === d.id ? 'Checking…' : 'Verify'}
                  </button>
                )}
                {canManage && (confirmRemove === d.id ? (
                  <>
                    <button className="ad-btn small danger" disabled={busyId === d.id} onClick={() => void remove(d)}>Confirm remove</button>
                    <button className="fc-act" onClick={() => setConfirmRemove(null)}>Cancel</button>
                  </>
                ) : (
                  <button className="fc-act" onClick={() => setConfirmRemove(d.id)}>Remove</button>
                ))}
              </div>
              {!d.verified_at && (
                <div style={{ fontSize: 13, lineHeight: 1.7, marginTop: 8, color: 'var(--ink-700)' }}>
                  Add these two records with your DNS provider, then press Verify:
                  <div style={{ margin: '6px 0 2px' }}>
                    <b>TXT</b> <span style={mono}>_form-magic.{d.domain}</span> → <span style={mono}>{d.verify_token}</span>
                  </div>
                  <div>
                    <b>CNAME</b> <span style={mono}>{d.domain}</span> → <span style={mono}>{state.cnameTarget}</span>
                  </div>
                </div>
              )}
              {d.verified_at && d.render_status === 'verified' && (
                <div className="hint" style={{ marginTop: 6 }}>
                  Live: share links as https://{d.domain}/&lt;form-slug&gt;. Your form-magic.com links keep working too.
                </div>
              )}
              {d.last_error && <div className="hint" style={{ marginTop: 6, color: 'var(--warn-fg)' }}>{d.last_error}</div>}
            </div>
          );
        })}

        {configured && canManage && (
          <form onSubmit={add} style={{ display: 'flex', gap: 8, marginTop: domains.length ? 12 : 0, flexWrap: 'wrap' }}>
            <input
              aria-label="Domain to add" placeholder="forms.your-company.com" value={adding}
              onChange={(e) => setAdding(e.target.value)} style={{ flex: 1, minWidth: 220 }}
            />
            <button className="fs-btn primary" type="submit" disabled={busyId === 'add' || !adding.trim()}>
              {busyId === 'add' ? 'Adding…' : 'Add domain'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
